import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormInput } from "../../../components/FormInput";
import PanelDataTable from "../../../components/PanelDataTable";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { queryCompanyProduct } from "../../../utility/service/carbonFootprint-service";
import {
  F1060302Before,
  F1060303Before,
  createCbamInspection,
  queryCbamInspectionDetail,
  updateCbamInspection,
} from "../../../utility/service/cbam-service";
import { checkDpSuccess, getFormErrorMessage, setformate } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { MaskedFormInput } from "../../../components/MaskedFormInput";
import { max } from "bignumber.js";

const InventoryInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  //表單驗證
  const [formRule, setFormRule] = useState([]);
  // 產品資訊列表
  const [productList, setProductList] = useState([]);
  const [placeName, setPlaceName] = useState("");
  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  const [delData, setDelData] = useState([]);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [clearFlag, setClearFlag] = useState(false);
  const [oriData, setOriData] = useState({});
  const [maskView, setMaskView] = useState(false);

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    if (currentCompany) {
      axios_queryCompanyProduct({ companySeq: currentCompany?.companySeq });
    }
  }, [currentCompany]);

  useEffect(() => {
    if (action === "create") {
      handleCreate();
      addForm({ offeringsId: "" });
      setTabAction("create");
      setMaskView(true);
    } else if (action === "detail") {
      handleEdit();
      axios_queryCbamInspectionDetail({ companySeq: currentCompany?.companySeq, cbamMasterSeq: currentCbamMasterSeq });
    } else {
      btnBackTo();
    }
  }, [action, currentCbamMasterSeq]);

  const btnBackTo = () => {
    navigate("/management/cbam");
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidDetailForm = useCallback(
    (formId) => {
      if (setValidForm("cbamDetailList")?.length > 0) {
        const tmp = setValidForm("cbamDetailList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamDetailList")],
  );

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const { fields: cbamDetailFields, append: addForm, remove: delForm } = useFieldArray({
    control,
    name: "cbamDetailList",
  });

  const handleResetForm = (fieldArrayApi, defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
    fieldArrayApi.append(defaultList);
  };

  const handleDelForm = (fieldArrayApi, defaultList, index, length) => {
    clearErrors();
    const removedItem = cbamDetailFields[index];
    if (removedItem?.cbamDetailSeq !== "" && removedItem?.cbamDetailSeq !== undefined) {
      setDelData((prev) => [...prev, { ...removedItem, recordStatus: "delete" }]);
    }
    fieldArrayApi.remove(index);
    setClearFlag(true);
    if (length <= 1) {
      fieldArrayApi.append(defaultList);
    }
  };

  const detailPanelConfig = {
    header: t("declareProductWeight"),
  };

  const detailDataTableConfig = {
    dataKey: "id",
    value: cbamDetailFields,
    footerEvent: () => {
      addForm({});
      setClearFlag(true);
    },
    className: `${editFlag ? "last-row-no-border" : ""}`,
    footerDisabled: editFlag,
  };

  const detailColumnsConfig = [
    // 名稱
    {
      field: "offeringsId",
      fieldName: "offeringsId",
      fieldListName: "cbamDetailList",
      header: t("product_name"),
      headerRequired: tabAction !== "detail",
      width: "45%",
      maxWidth: "200px",
      type: "dropdown",
      control: control,
      rules: setValidDetailForm("offeringsId"),
      options: productList,
      optionLabel: "label",
      optionValue: "value",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      nonEditor: (value) => value?.cbamDetailSeq,
      displayText: (rowData, rowIndex) => productList.filter((item) => item.value === rowData?.offeringsId)[0]?.label,
    },
    // 申報產品貨重(公噸)
    {
      field: "declareProductWeight",
      fieldName: "declareProductWeight",
      fieldListName: "cbamDetailList",
      header: t("declareProductWeight"),
      headerRequired: tabAction !== "detail",
      width: "45%",
      maxWidth: "200px",
      type: "number",
      control: control,
      rules: setValidDetailForm("declareProductWeight"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamDetailList.${rowIndex}.declareProductWeight`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamDetailList.${rowIndex}.declareProductWeight`, newValue);
      },
    },
  ];

  const detailActionConfig = {
    fields: cbamDetailFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delForm, append: addForm },
    defaultList: {},
    disabled: editFlag,
  };

  const handleCreate = async () => {
    const res = await F1060302Before();
    if (checkDpSuccess(res)) {
      setFormRule(processConstraints(res?.data?.RespBody?.constraints));
    }
  };

  const handleEdit = async () => {
    const res = await F1060303Before();
    if (checkDpSuccess(res)) {
      setFormRule(processConstraints(res?.data?.RespBody?.constraints));
    }
  };

  const handleMaskClick = () => {
    setMaskView((prev) => {
      return !prev;
    });
  };

  // 產品列表
  const axios_queryCompanyProduct = (reqBody, readMore = false) => {
    queryCompanyProduct(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res?.data?.RespBody?.evaluationTemplates?.options;
        setProductList(data);
      }
    });
  };

  // CBAM 盤查新增
  const axios_createCbamInspection = (data) => {
    checkLoading(loading);
    createCbamInspection(data)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data.RespBody;
          handleSuccessMessage(dispatch, t("button.create") + " " + t("message.success"));
          setCurrentCbamMasterSeq(data?.cbamMasterSeq);
          axios_queryCbamInspectionDetail({
            companySeq: currentCompany?.companySeq,
            cbamMasterSeq: data?.cbamMasterSeq,
          });
          setActiveIndex(1);
          setAction("detail");
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // CBAM 盤查更新
  const axios_updateCbamInspection = () => {
    checkLoading(loading);
    const reqBody = {
      cbamMasterSeq: currentCbamMasterSeq,
      cbamProjectName: getValues("cbamProjectName"),
      companySeq: currentCompany?.companySeq,
      offeringsId: getValues("offeringsId"),
      declareProductWeight: getValues("declareProductWeight"),
      placeSeq: getValues("placeSeq"),
      reportingStartDate: setformate(getValues("reportingStartDate"), "YYYY/MM/DD"),
      reportingEndDate: setformate(getValues("reportingEndDate"), "YYYY/MM/DD"),
      unlocode: getValues("unlocode"),
      geocodingLat: getValues("geocodingLat"),
      geocodingLng: getValues("geocodingLng"),
      verificationAgency: getValues("verificationAgency"),
      verificationCountry: getValues("verificationCountry"),
      verificationCity: getValues("verificationCity"),
      verificationAddress: getValues("verificationAddress"),
      verificationPostcode: getValues("verificationPostcode"),
      verificationName: getValues("verificationName"),
      verificationEmail: getValues("verificationEmail"),
      verificationTelephone: getValues("verificationTelephone"),
      verificationFax: getValues("verificationFax"),
      verificationMemberState: getValues("verificationMemberState"),
      verificationAccreditation: getValues("verificationAccreditation"),
      verificationRegistrationNumber: getValues("verificationRegistrationNumber"),
      cbamDetailFlag: clearFlag ? "Y" : "N",
      cbamDetailList: [
        ...delData,
        ...getValues("cbamDetailList").map((item) => {
          const recordStatus = item?.cbamDetailSeq === "" || item?.cbamDetailSeq === undefined ? "insert" : "update";
          return { ...item, recordStatus };
        }),
      ],
    };

    updateCbamInspection(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(1);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
        setConfirmPopup(false);
      });
  };

  // CBAM 盤查詳情查詢
  const axios_queryCbamInspectionDetail = (reqBody) => {
    checkLoading(loading);
    queryCbamInspectionDetail(reqBody)
      .then((res) => {
        let data = res?.data.RespBody;
        if (checkDpSuccess(res)) {
          setItemObj(data);
          setPlaceName(currentCompany?.placeList?.filter((item) => item.placeSeq === data.placeSeq)[0]?.address);
          const oriData = {
            cbamMasterSeq: currentCbamMasterSeq,
            cbamProjectName: data?.cbamProjectName,
            companySeq: currentCompany?.companySeq,
            placeSeq: data?.placeSeq,
            reportingStartDate: new Date(data?.reportingStartDate),
            reportingEndDate: new Date(data?.reportingEndDate),
            unlocode: data?.unlocode,
            geocodingLat: data?.geocodingLat,
            geocodingLng: data?.geocodingLng,
            verificationAgency: data?.verificationAgency,
            verificationCountry: data?.verificationCountry,
            verificationCity: data?.verificationCity,
            verificationAddress: data?.verificationAddress,
            verificationPostcode: data?.verificationPostcode,
            verificationName: data?.verificationName,
            verificationEmail: data?.verificationEmail,
            verificationTelephone: data?.verificationTelephone,
            verificationFax: data?.verificationFax,
            verificationMemberState: data?.verificationMemberState,
            verificationAccreditation: data?.verificationAccreditation,
            verificationRegistrationNumber: data?.verificationRegistrationNumber,
            cbamDetailList: data?.cbamDetailList,
          };

          setOriData(oriData);
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value, { shouldValidate: true });
          });
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const onSubmit = (data) => {
    let reqBody = {
      cbamMasterSeq: currentCbamMasterSeq,
      cbamProjectName: data.cbamProjectName,
      companySeq: currentCompany?.companySeq,
      offeringsId: data.offeringsId,
      declareProductWeight: data.declareProductWeight,
      placeSeq: data.placeSeq,
      reportingStartDate: setformate(data.reportingStartDate, "YYYY/MM/DD"),
      reportingEndDate: setformate(data.reportingEndDate, "YYYY/MM/DD"),
      unlocode: data.unlocode,
      geocodingLat: data.geocodingLat,
      geocodingLng: data.geocodingLng,
      verificationAgency: data.verificationAgency,
      verificationCountry: data.verificationCountry,
      verificationCity: data.verificationCity,
      verificationAddress: data.verificationAddress,
      verificationPostcode: data.verificationPostcode,
      verificationName: data.verificationName,
      verificationEmail: data.verificationEmail,
      verificationTelephone: data.verificationTelephone,
      verificationFax: data.verificationFax,
      verificationMemberState: data.verificationMemberState,
      verificationAccreditation: data.verificationAccreditation,
      verificationRegistrationNumber: data.verificationRegistrationNumber,
      cbamDetailList: data.cbamDetailList.map((item) => {
        return {
          ...item,
          recordStatus: "insert",
        };
      }),
    };
    if (action === "create") {
      axios_createCbamInspection(reqBody);
    } else {
      if (clearFlag) {
        setConfirmPopup(true);
      } else {
        axios_updateCbamInspection();
      }
    }
  };

  return (
    <>
      <form className="grid" onSubmit={handleSubmit(onSubmit)}>
        {/* 專案名稱 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            labelClass={`${tabAction === "detail" ? "" : "required"}`}
            id="cbamProjectName"
            name="cbamProjectName"
            type="text"
            labelText={t("projectName")}
            rules={setValidForm("cbamProjectName")}
          />
        </div>
        {/* 報告期間 */}
        <div className="col-12 lg:col-8">
          <label htmlFor="reportingStartDate" className={`form-label ${tabAction === "detail" ? "" : "required"}`}>
            {t("reportingPeriod")}
          </label>
          <div className="col-date-picker">
            <Controller
              name="reportingStartDate"
              control={control}
              rules={{
                required: { value: true, message: t("validation.required") },
                validate: (val) => {
                  if (val !== "" && watch("reportingEndDate") !== "" && watch("reportingEndDate") < val)
                    return t("validation.startDate");
                },
              }}
              render={({ field, fieldState, formState }) => (
                <Calendar
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  dateFormat="yy-mm-dd"
                  disabled={action === "detail" || editFlag}
                  value={field.value || ""}
                  onChange={(e) => {
                    field.onChange(e.value);
                    if (e.value < watch("reportingEndDate")) {
                      clearErrors("reportingEndDate");
                    }
                  }}
                  showIcon
                />
              )}
            />
            {getFormErrorMessage(errors, "reportingStartDate")}

            <Controller
              name="reportingEndDate"
              control={control}
              rules={{
                required: { value: true, message: t("validation.required") },
                validate: (val) => {
                  if (val !== "" && watch("reportingStartDate") !== "" && watch("reportingStartDate") > val)
                    return t("validation.endDate");
                },
              }}
              render={({ field, fieldState, formState }) => (
                <Calendar
                  className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                  dateFormat="yy-mm-dd"
                  disabled={action === "detail" || editFlag}
                  value={field.value || ""}
                  onChange={(e) => {
                    field.onChange(e.value);
                    if (e.value > watch("reportingStartDate")) {
                      clearErrors("reportingStartDate");
                    }
                  }}
                  showIcon
                />
              )}
            />
            {getFormErrorMessage(errors, "reportingEndDate")}
          </div>
        </div>
        {/* 場所 */}
        <div className="col-12 lg:col-4">
          <label htmlFor="place" className={`form-label ${tabAction === "detail" ? "" : "required"}`}>
            {t("place")}
          </label>
          <Controller
            name="placeSeq"
            control={control}
            rules={setValidForm("placeSeq")}
            render={({ field, fieldState }) => (
              <Dropdown
                disabled={action === "detail" || editFlag}
                appendTo={"self"}
                optionLabel="placeName"
                optionValue="placeSeq"
                value={field.value}
                virtualScrollerOptions={{ itemSize: 40 }}
                defaultValue={""}
                placeholder={t("plz_chs")}
                name="place"
                control={control}
                options={currentCompany?.placeList || []}
                className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                onChange={(event) => {
                  field.onChange(event.value);
                  setPlaceName(currentCompany?.placeList?.filter((item) => item.placeSeq === event.value)[0]?.address);
                }}
              ></Dropdown>
            )}
          ></Controller>
        </div>
        {/* 地址 */}
        <div className="col-12 lg:col-8">
          <Controller
            name="placeName"
            control={control}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={placeName ?? ""}
                labelClass={`form-label`}
                labelText={t("address")}
                maskType="address"
                maskView={maskView || action === "create"}
                setMaskView={() => handleMaskClick()}
                disabled={true}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        {/* 經度 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="geocodingLat"
            name="geocodingLat"
            labelText={t("geocodingLat")}
            type="text"
            rules={setValidForm("geocodingLat")}
          />
        </div>
        {/* 緯度 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="geocodingLng"
            name="geocodingLng"
            labelText={t("geocodingLng")}
            type="text"
            rules={setValidForm("geocodingLng")}
          />
        </div>
        {/* UNLOCODE */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="unlocode"
            name="unlocode"
            labelText={"UNLOCODE"}
            type="text"
            rules={setValidForm("unlocode")}
          />
        </div>
        {/* 申報產品貨重(公噸) */}
        <div className="col-12">
          <PanelDataTable
            panelConfig={detailPanelConfig}
            dataTableConfig={detailDataTableConfig}
            columnsConfig={detailColumnsConfig}
            actionConfig={detailActionConfig}
          />
        </div>
        {/* 查驗證機構 */}
        <div className="col-12">
          <label className="font-semibold">{t("verificationAgency")}</label>({t("verificationAgencyHint")})
        </div>
        {/* 機構名稱 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationAgency"
            name="verificationAgency"
            labelText={t("verificationAgencyName")}
            type="text"
            rules={setValidForm("verificationAgencyName")}
          />
        </div>
        <div className="col-12"></div>
        {/* 國家 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationCountry"
            name="verificationCountry"
            labelText={t("country")}
            type="text"
            rules={setValidForm("verificationCountry")}
          />
        </div>
        {/* 城市 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationCity"
            name="verificationCity"
            labelText={t("city")}
            type="text"
            rules={setValidForm("verificationCity")}
          />
        </div>
        {/* 郵遞區號 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationPostcode"
            name="verificationPostcode"
            labelText={t("postCode")}
            type="text"
            rules={setValidForm("verificationPostcode")}
          />
        </div>
        {/* 機構地址 */}
        <div className="col-12">
          <Controller
            name="verificationAddress"
            control={control}
            rules={setValidForm("verificationAddress")}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={field.value ?? ""}
                labelClass={`form-label`}
                labelText={t("verificationAgencyAddress")}
                maskType="address"
                maskView={maskView}
                setMaskView={() => handleMaskClick()}
                disabled={editFlag}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        {/* 授權代表姓名 */}
        <div className="col-12 lg:col-4">
          <Controller
            name="verificationName"
            control={control}
            rules={setValidForm("verificationName")}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={field.value ?? ""}
                labelClass={`form-label`}
                labelText={t("verificationName")}
                maskType="name"
                maskView={maskView}
                setMaskView={() => handleMaskClick()}
                disabled={editFlag}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        <div className="col-12"></div>
        {/* 電子信箱 */}
        <div className="col-12 lg:col-4">
          <Controller
            name="verificationEmail"
            control={control}
            rules={setValidForm("verificationEmail")}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={field.value ?? ""}
                labelClass={`form-label`}
                labelText={t("companyEamil")}
                maskType="email"
                maskView={maskView}
                setMaskView={() => handleMaskClick()}
                disabled={editFlag}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        {/* 電話號碼 */}
        <div className="col-12 lg:col-4">
          <Controller
            name="verificationTelephone"
            control={control}
            rules={setValidForm("verificationTelephone")}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={field.value ?? ""}
                labelClass={`form-label`}
                labelText={t("verificationTelephone")}
                maskType="phone"
                maskView={maskView}
                setMaskView={() => handleMaskClick()}
                disabled={editFlag}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        {/* 傳真 */}
        <div className="col-12 lg:col-4">
          <Controller
            name="verificationFax"
            control={control}
            rules={setValidForm("verificationFax")}
            render={({ field, fieldState }) => (
              <MaskedFormInput
                {...field}
                type="text"
                value={field.value ?? ""}
                labelClass={`form-label`}
                labelText={t("verificationFax")}
                maskType="phone"
                maskView={maskView}
                setMaskView={() => handleMaskClick()}
                disabled={editFlag}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </div>
        {/* 認證成員國 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationMemberState"
            name="verificationMemberState"
            labelText={t("verificationMemberState")}
            type="text"
            rules={setValidForm("verificationMemberState")}
          />
        </div>
        {/* 國家認證機構名稱 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationAccreditation"
            name="verificationAccreditation"
            labelText={t("verificationAccreditation")}
            type="text"
            rules={setValidForm("verificationAccreditation")}
          />
        </div>
        {/* 註冊編號 */}
        <div className="col-12 lg:col-4">
          <FormInput
            disabled={editFlag}
            register={register}
            errors={errors}
            id="verificationRegistrationNumber"
            name="verificationRegistrationNumber"
            labelText={t("verificationRegistrationNumber")}
            type="text"
            rules={setValidForm("verificationRegistrationNumber")}
          />
        </div>

        <div className="col-12 gap-2 flex-all-center">
          {tabAction === "detail" && (
            <Button
              disabled={!isEditable}
              type="button"
              label={t("button.edit")}
              size="small"
              onClick={() => {
                setTabAction("create");
                setMaskView(true);
              }}
            />
          )}

          {tabAction === "create" && (
            <>
              <Button
                disabled={!(isValid && isDirty)}
                type="submit"
                label={action === "create" ? t("button.create") : t("button.update")}
                size="small"
              />
              {action !== "create" && (
                <Button
                  outlined
                  size="small"
                  label={t("button.cancel")}
                  onClick={(e) => {
                    e.preventDefault();
                    clearErrors();
                    setTabAction("detail");
                    reset(oriData);
                    setClearFlag(false);
                    setMaskView(false);
                  }}
                />
              )}
            </>
          )}
        </div>
      </form>

      <PrimeDialog
        visible={confirmPopup}
        onClose={() => {
          setConfirmPopup(false);
        }}
        onConfirm={() => {
          axios_updateCbamInspection();
        }}
        title={t("hint")}
        size="sm"
        bodyTemplate={t(t("inverntryCbamDetailClearTip"))}
      />
    </>
  );
};
export default InventoryInfo;
